import type { Kind, SectionId } from './types';

/**
 * A quick "is that right?" while counting — catches typos (210 for 21) on the spot, before a
 * manager ever sees them. It never blocks: staff can say it's right and carry on.
 *
 *  back beer / liquor  more than last night + what was brought up or delivered tonight, by
 *                      more than 6 bottles or 3 oz; or much less (24+ bottles and more than half,
 *                      or 20+ oz and more than 60 %).
 *  front bar beer      it's refilled every night, so it's compared with its usual amount
 *                      (more than 1½ × usual + 6) rather than with last night.
 *  anything            far beyond what's ever been there (3× last night and 48+ more).
 */

export interface EntryCheckInput {
  kind: Kind;
  section: SectionId;
  value: number | null;
  /** The last count of this drink on this list (within a week), if any. */
  prev: number | null;
  /** Brought up or delivered tonight for this drink. */
  added: number;
  usual: number | null;
}

const n = (kind: Kind, v: number) => `${Math.round(v * 100) / 100}${kind === 'liquor' ? ' oz' : ''}`;

export function entryWarning(i: EntryCheckInput): string | null {
  const { kind, section, value, prev, added } = i;
  if (value == null) return null;
  if (prev != null && value > Math.max(prev * 3, prev + added + 48) && value > 10) return `That’s a lot more than last night (${n(kind, prev)}). Check for an extra digit.`;
  if (section === 'front_beer') {
    if (i.usual != null && value > i.usual * 1.5 + 6) return `That’s a lot more than usual for this cooler (${i.usual}). Is it right?`;
    return null;
  }
  if (prev == null) return value > (kind === 'beer' ? 500 : 400) ? 'That’s a big number. Is it right?' : null;
  const most = prev + added;
  const slack = kind === 'beer' ? 6 : 3;
  if (value > most + slack)
    return added > 0
      ? `That’s ${n(kind, value - most)} more than last night (${n(kind, prev)}) plus what came up tonight (${n(kind, added)}). Is it right? If more came up, add it under “Brought from downstairs”.`
      : `That’s ${n(kind, value - prev)} more than last night (${n(kind, prev)}). Is it right? If some came up from downstairs, add it under “Brought from downstairs”.`;
  const drop = most - value;
  if (kind === 'beer' ? drop >= 24 && drop > most * 0.5 : drop >= 20 && drop > most * 0.6) return `That’s a lot less than last night (${n(kind, prev)}${added ? ` + ${n(kind, added)} brought up` : ''}). Is it right?`;
  return null;
}
